import React, { useEffect, useState } from 'react';
import './EAction.css';
import { useAuth } from '@/components/auth/AuthContext';
import { useI18n } from '@/i18n';

type ActionKind = 'warehouse' | 'procurement' | 'view' | 'view_dept' | 'view_pending' | 'purchase_start' | 'warehouse_start' | 'warehouse_management' | 'moderator_pending' | 'view_database' | 'ai_chatbot';

const EAction: React.FC<{ onSelect?: (action: ActionKind) => void }> = ({ onSelect }) => {
  const { user } = useAuth();
  const { t } = useI18n();

  // Show for EMPLOYEE or HEAD level and non-warehouse/procurement departments
  const levelId = Number((user as any)?.level);
  const levelOk = levelId === 1 || levelId === 2 || levelId === 3;
  const deptId = Number((user as any)?.department_id);
  const deptNameRaw = (user as any)?.department_name || '';
  const deptName = deptNameRaw?.toUpperCase?.() || '';
  const blocked = ['PROCUREMENT']; // Do not block WAREHOUSE anymore
  const deptOk = deptName ? !blocked.includes(deptName) : true;
  const isSupplyChain = (deptId === 25) || deptName === 'SUPPLY CHAIN';
  const isFinance = (deptId === 34) || (deptName.includes('FINANCE') && (deptName.includes('ACCOUNT') || deptName.includes('ACCT')));
  const isWarehouse = deptId === 7 || deptName === 'WAREHOUSE';
  const isHead = levelId === 2;
  const isModerator = levelId === 3;

  const [mounted, setMounted] = useState(false); // button entrance animation
  const [panelMounted, setPanelMounted] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    // Animate button appearing from the left on mount
    const t = setTimeout(() => setMounted(true), 20);
    return () => clearTimeout(t);
  }, []);

  if (!levelOk || !deptOk) return null;

  const openPanel = () => {
    setPanelMounted(true);
    setTimeout(() => setPanelOpen(true), 20);
  };
  const closePanel = () => {
    setPanelOpen(false);
    setTimeout(() => setPanelMounted(false), 320);
  };

  return (
    <>
      {/* Keep trigger mounted, fade/slide it out while panel is open */}
      <button
        type="button"
        className={`action-trigger ${mounted ? 'enter' : ''} ${panelMounted ? 'hidden' : ''}`}
        aria-label={t('actions.openActions')}
        onClick={openPanel}
      >
        <span className="action-icon" aria-hidden>
          {/* Lightning icon */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" stroke="currentColor" strokeWidth="1.8" fill="none" />
          </svg>
        </span>
      </button>

      {panelMounted && (
        <div className={`eaction-overlay ${panelOpen ? 'open' : ''}`} onClick={closePanel}>
          <div className={`eaction-panel ${panelOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="eaction-header">
              <div className="eaction-title">{t('actions.title')}</div>
              <button type="button" className="close-btn" onClick={closePanel} aria-label={t('common.close')}>×</button>
            </div>
            <div className="eaction-body">
              <div className="eaction-grid">
                {/* NEW: VIEW DATABASE (top-most). Visible to Warehouse, Supply Chain, Finance */}
                {(isWarehouse || isSupplyChain || isFinance) && (
                  <button type="button" className="eaction-btn db" onClick={() => { onSelect?.('view_database'); closePanel(); }}>
                    {t('actions.viewDatabase')}
                  </button>
                )}
                {/* Action 1: WAREHOUSE MANAGEMENT (Warehouse EMPLOYEE or HEAD) - Move to top */}
                {(isWarehouse) && (
                  <button type="button" className="eaction-btn" onClick={() => { onSelect?.('warehouse_management'); closePanel(); }}>
                    {t('actions.warehouseManagement')}
                  </button>
                )}

                {/* Action 2: CREATE WAREHOUSE REQUEST */}
                <button type="button" className="eaction-btn" onClick={() => { onSelect?.('warehouse'); closePanel(); }}>
                  {t('actions.createWarehouseRequest')}
                </button>

                {/* Action 3: CREATE PURCHASE REQUEST */}
                <button type="button" className="eaction-btn" onClick={() => { onSelect?.('procurement'); closePanel(); }}>
                  {t('actions.createPurchaseRequest')}
                </button>

                {/* Action 4: VIEW YOUR REQUESTS */}
                <button type="button" className="eaction-btn" onClick={() => { onSelect?.('view'); closePanel(); }}>
                  {t('actions.viewYourRequests')}
                </button>

                {/* Action 5: VIEW DEPARTMENT REQUESTS (for HEAD). */}
                {isHead && (
                  <button type="button" className="eaction-btn" onClick={() => { onSelect?.('view_dept'); closePanel(); }}>
                    {t('actions.viewDepartmentRequests')}
                  </button>
                )}

                {/* Action 6: VIEW PENDING REQUESTS (Supply Chain or Finance EMPLOYEE/HEAD) */}
                {(isSupplyChain || isFinance) && (
                  <button type="button" className="eaction-btn" onClick={() => { onSelect?.('view_pending'); closePanel(); }}>
                    {t('actions.viewPendingRequests')}
                  </button>
                )}

                {/* Action 6b: VIEW PENDING REQUESTS (MODERATOR - category scoped) */}
                {isModerator && (
                  <button type="button" className="eaction-btn" onClick={() => { onSelect?.('moderator_pending'); closePanel(); }}>
                    {t('actions.viewPendingRequests')}
                  </button>
                )}
                {/* Action 7: PURCHASE START (Supply Chain EMPLOYEE or HEAD) */}
                {(isSupplyChain) && (
                  <button type="button" className="eaction-btn" onClick={() => { onSelect?.('purchase_start'); closePanel(); }}>
                    {t('actions.purchaseStart')}
                  </button>
                )}
                {/* Action 8: WAREHOUSE PENDING (Warehouse EMPLOYEE or HEAD) */}
                {(isWarehouse) && (
                  <button type="button" className="eaction-btn" onClick={() => { onSelect?.('warehouse_start'); closePanel(); }}>
                    {t('actions.viewPendingRequests')}
                  </button>
                )}

                {/* Action: AI CHATBOT - visible to Warehouse, Supply Chain, Finance */}
                {(isWarehouse || isSupplyChain || isFinance) ? (
                  <button type="button" className="eaction-btn" onClick={() => { onSelect?.('ai_chatbot'); closePanel(); }}>
                    {t('actions.aiChatbot')}
                  </button>
                ) : (
                  <button type="button" className="eaction-btn" disabled aria-disabled="true" title={t('actions.aiChatbotRestricted')}>
                    {t('actions.aiChatbot')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EAction;
